import { ValidatorsUtil } from "@helper/validators-util";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { DomSanitizer } from "@angular/platform-browser";
import { UserState } from "@app/user/user.reducer";
import { Actions, ofType } from "@ngrx/effects";
import { createSelector, select, Store } from "@ngrx/store";
import { OverlayComponent } from "@shared/overlay/overlay.component";
import { Observable, Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { UserErrorsService } from "@app/user/user-core/services/user-errors.service";
import { UserRoutingService } from "@app/user/user-core/services/user-routing.service";
import { notNull } from "@helper/operators";
import { MobileEdsDto } from "@helper/abstraction/user";
import { UserBackendService } from "@app/user/user-core/services/user-backend.service";
import { authorizeCryptoMobile, attachCryptoMobile, authorizeCryptoMobileSuccess, attachCryptoMobileSuccess } from "@app/user/customization/customization-store/sccrypto.actions";
import { environment } from "src/environments/environment";

@Component({
	selector: "app-certification-mobile-popup",
	templateUrl: "./certification-mobile-popup.component.html",
	styleUrls: ["./certification-mobile-popup.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CertificationMobilePopupComponent implements OnInit {
	@Input() public isAuthorizeMobileEds = false;
	@Input() public urlScMobileForm: any;
	@ViewChild(OverlayComponent, { static: false }) public readonly overlay?: OverlayComponent;
	public close$: Subject<boolean | void> = new Subject<boolean | void>();
	public form: FormGroup;
	public mobileEds$: Observable<MobileEdsDto | undefined>;
	public isFirst = true;
	public env = environment;

	private unsubscribe$$: Subject<void> = new Subject<void>();

	constructor(
		private sanitizer: DomSanitizer,
		private actions$: Actions,
		private fB: FormBuilder,
		private store: Store<UserState>,
		private userErrorsService: UserErrorsService,
		private userBackendService: UserBackendService,
		private cdr: ChangeDetectorRef,
		private userRoutingService: UserRoutingService,
	) {
		const selectUser = (appState: any): UserState | any => appState.user;
		const getMobileEds = createSelector(selectUser, (state: UserState): MobileEdsDto | undefined => state.userProfile?.mobileEds);
		this.mobileEds$ = this.store.pipe(select(getMobileEds));

		this.form = this.fB.group({ phoneNumber: [null, [Validators.required, ValidatorsUtil.checkLength(9)]] });
		window.addEventListener("message", this.listener);
	}

	public listener = (event: any): any => {
		if (event.data?.hasOwnProperty("method") && this.isFirst) {
			this.openObservable(event.data);
		}
	};

	public ngOnInit(): void {
		this.mobileEds$.pipe(notNull(), takeUntil(this.unsubscribe$$)).subscribe((mobileEds) => {
			if (mobileEds?.type === "PHONE" && mobileEds?.phoneNumber) {
				this.form?.get("phoneNumber")?.patchValue(mobileEds?.phoneNumber.slice(4));
				this.cdr.detectChanges();
			} else if (mobileEds?.type === "ID_CARD") {
				this.form?.get("phoneNumber")?.clearValidators();
				this.form?.get("phoneNumber")?.updateValueAndValidity({ emitEvent: true });
				this.cdr.detectChanges();
			}
			this.confirmCertificate();
        });
	}

	public confirmCertificate(): void {
		ValidatorsUtil.triggerValidation(this.form);
		if (this.form.invalid) {
			this.userErrorsService.displayErrors(this.form);
			return;
		}
		if (this.isAuthorizeMobileEds) {
			this.store.dispatch(authorizeCryptoMobile());
			this.actions$.pipe(
				ofType(authorizeCryptoMobileSuccess),
				take(1)
			).subscribe((value) => {
				this.urlScMobileForm = value.mobileEds;
				this.cdr.detectChanges();
			});
			return;
		}

		const mobileEds = {
			phoneNumber: "+375" + this.form.value?.phoneNumber,
			authUrl: this.form.value?.authUrl ? this.form.value?.authUrl : null,
		};
		this.store.dispatch(attachCryptoMobile(mobileEds));
		this.actions$.pipe(
			ofType(attachCryptoMobileSuccess),
			take(1)
		).subscribe((value) => {
			this.urlScMobileForm = value.mobileEds;
			this.cdr.detectChanges();
		});
	}

	public get url(): any {
		return this.sanitizer.bypassSecurityTrustResourceUrl(this.urlScMobileForm.authUrl);
	}

	public close(isClosed: boolean): void {
		this.close$.next(isClosed);
	}

	public ngOnDestroy(): void {
		this.unsubscribe$$.next();
		this.unsubscribe$$.complete();
		window.removeEventListener("message", this.listener);
	}

	private openObservable(data: any): void {
		this.isFirst = false;
		window.removeEventListener("message", this.listener);
		let obs = null;
		if (data?.method === "callback") {
			obs = this.userBackendService.crypto.callbackCryptoMobile.post$(data?.search);
		}
		if (!obs) {
			throw "no method from service";
		}
		obs
			.pipe(takeUntil(this.unsubscribe$$))
			.subscribe((value) => {
				++this.userRoutingService.steps;
				if (value.status === "COMPLETED") {
					this.close(!!this.urlScMobileForm);
				} else {
					this.close(false);
				}
			});
	}
}
